/**
 * Custom Mega Menu Web Component
 * Handles open/close behavior with hover on desktop, tap on mobile
 * Menu link is clickable and navigates to URL
 */

class CustomMegaMenu extends HTMLElement {
  constructor() {
    super();
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.content = this.querySelector('.custom-mega-menu__content');
    this.overlay = this.querySelector('.custom-mega-menu__overlay');
    this.closeBtn = this.querySelector('.custom-mega-menu__close-btn');
    this.linkTitle = this.querySelector('.custom-mega-menu__link-title');
    this.isDesktop = window.matchMedia('(min-width: 990px)');
    this.openTimeout = null;
    this.closeTimeout = null;
    this.boundCloseOnOutsideClick = this.handleOutsideClick.bind(this);
    this.boundHandleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    this.setupEventListeners();
  }

  disconnectedCallback() {
    this.clearTimeouts();
    document.removeEventListener('click', this.boundCloseOnOutsideClick);
    document.removeEventListener('keydown', this.boundHandleKeydown);
  }

  clearTimeouts() {
    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
      this.openTimeout = null;
    }
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }

  setupEventListeners() {
    // Desktop: Hover to open/close with delay
    this.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
    this.addEventListener('mouseleave', this.handleMouseLeave.bind(this));

    // Link click - allow navigation (don't prevent default)
    // The anchor inside summary will navigate naturally

    // Summary click - for mobile toggle (only on the caret area)
    this.summary?.addEventListener('click', (event) => {
      // If clicking on the anchor link, allow navigation
      if (event.target.closest('.custom-mega-menu__link-title')) {
        // On desktop, prevent navigation and let hover handle menu
        if (this.isDesktop.matches) {
          // Allow the click to navigate
          return;
        } else {
          // On mobile, prevent navigation and toggle menu instead
          event.preventDefault();
          if (this.isOpen()) {
            this.close();
          } else {
            this.open();
          }
        }
        return;
      }

      // Clicking on caret or other summary area - toggle menu
      event.preventDefault();
      if (this.isOpen()) {
        this.close();
      } else {
        this.open();
      }
    });

    // Overlay click to close
    this.overlay?.addEventListener('click', () => {
      this.close();
    });

    // Close button (mobile)
    this.closeBtn?.addEventListener('click', () => {
      this.close();
    });

    // Escape key to close
    document.addEventListener('keydown', this.boundHandleKeydown);

    // Click outside to close
    document.addEventListener('click', this.boundCloseOnOutsideClick);

    // Focus management - close when focus leaves mega menu (with delay)
    this.addEventListener('focusout', (event) => {
      setTimeout(() => {
        if (this.isOpen() && !this.contains(document.activeElement)) {
          this.close();
        }
      }, 150);
    });

    // Listen for animation end on content to complete close
    this.content?.addEventListener('animationend', (event) => {
      if (event.animationName === 'megaMenuSlideOut' || event.animationName === 'megaMenuSlideOutMobile') {
        this.completeClose();
      }
    });
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.isOpen()) {
      this.close();
      this.summary?.focus();
    }
  }

  handleMouseEnter() {
    if (!this.isDesktop.matches) return;

    this.clearTimeouts();

    // If closing, cancel and reopen
    if (this.details?.classList.contains('is-closing')) {
      this.details.classList.remove('is-closing');
    }

    // Small delay before opening
    this.openTimeout = setTimeout(() => {
      this.open();
    }, 80);
  }

  handleMouseLeave() {
    if (!this.isDesktop.matches) return;

    this.clearTimeouts();

    // Delay before closing
    this.closeTimeout = setTimeout(() => {
      this.close();
    }, 150);
  }

  handleOutsideClick(event) {
    if (!this.isOpen()) return;

    // Check if click is outside the mega menu
    if (!this.contains(event.target)) {
      this.close();
    }
  }

  isOpen() {
    return this.details?.hasAttribute('open') && !this.details?.classList.contains('is-closing');
  }

  open() {
    if (!this.details || this.isAnimating) return;
    if (this.details.hasAttribute('open') && !this.details.classList.contains('is-closing')) return;

    // If was closing, cancel it
    if (this.details.classList.contains('is-closing')) {
      this.details.classList.remove('is-closing');
    }

    // Close any other open mega menus first
    document.querySelectorAll('custom-mega-menu').forEach(menu => {
      if (menu !== this) {
        const menuDetails = menu.querySelector('details');
        if (menuDetails?.hasAttribute('open')) {
          menu.close();
        }
      }
    });

    // Close any other open header-menu details
    document.querySelectorAll('header-menu details[open]').forEach(details => {
      details.removeAttribute('open');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });

    this.details.setAttribute('open', '');
    this.summary?.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (!this.details || !this.details.hasAttribute('open')) return;
    if (this.details.classList.contains('is-closing')) return;

    // Add closing class to trigger close animation
    this.details.classList.add('is-closing');
    this.summary?.setAttribute('aria-expanded', 'false');

    // Fallback: if animationend doesn't fire, close after timeout
    setTimeout(() => {
      if (this.details?.classList.contains('is-closing')) {
        this.completeClose();
      }
    }, 300);
  }

  completeClose() {
    if (!this.details) return;

    this.details.classList.remove('is-closing');
    this.details.removeAttribute('open');
  }
}

// Register custom element
if (!customElements.get('custom-mega-menu')) {
  customElements.define('custom-mega-menu', CustomMegaMenu);
}
